import React from 'react';
import { motion } from 'framer-motion';

/**
 * Props for the {@link OnboardingProgressV2} component
 */
interface OnboardingProgressV2Props {
  /** The current active step in the onboarding flow */
  currentStep: 'snowflake_setup' | 'runner_provision';
}

/**
 * Onboarding step definition
 */
interface OnboardingStep {
  id: string;
  name: string;
  stepId: 'signup' | 'snowflake_setup' | 'runner_provision';
  status: 'complete' | 'current' | 'pending';
}

/**
 * OnboardingProgressV2 Component
 *
 * Displays a visual progress indicator showing the onboarding steps
 * and highlighting the current step. Uses OnboardingV2's horizontal stepper design
 * with improved visual styling.
 *
 * @component
 * @example
 * ```tsx
 * <OnboardingProgressV2 currentStep="snowflake_setup" />
 * ```
 *
 * @param props - Component props
 * @param props.currentStep - The current step in the onboarding process
 * @returns Progress indicator with step visualization
 */
const OnboardingProgressV2: React.FC<OnboardingProgressV2Props> = ({ currentStep }) => {
  /**
   * Generates the step configuration based on current step
   */
  const getSteps = (): OnboardingStep[] => {
    const baseSteps: Array<Omit<OnboardingStep, 'status'>> = [
      {
        id: '01',
        name: 'Sign up for DataOps.live',
        stepId: 'signup',
      },
      {
        id: '02',
        name: 'Connect your Snowflake account',
        stepId: 'snowflake_setup',
      },
      {
        id: '03',
        name: 'Get DataOps Runner',
        stepId: 'runner_provision',
      },
    ];

    // Define step order for determining completion status
    const stepOrder: Array<'signup' | 'snowflake_setup' | 'runner_provision'> = [
      'signup',
      'snowflake_setup',
      'runner_provision',
    ];
    const currentStepIndex = stepOrder.indexOf(currentStep);

    return baseSteps.map((step) => {
      const stepIndex = stepOrder.indexOf(step.stepId);
      let status: 'complete' | 'current' | 'pending';

      // Signup step is always complete
      if (step.stepId === 'signup') {
        status = 'complete';
      } else if (step.stepId === currentStep) {
        status = 'current';
      } else if (stepIndex < currentStepIndex) {
        status = 'complete';
      } else {
        status = 'pending';
      }

      return { ...step, status };
    });
  };

  const steps = getSteps();

  return (
    <nav aria-label="Onboarding progress" role="navigation">
      <ol role="list" className="space-y-4 md:flex md:space-x-8 md:space-y-0">
        {steps.map((step, index) => (
          <motion.li
            key={step.name}
            className="md:flex-1"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{
              duration: 0.4,
              delay: index * 0.1,
              ease: 'easeOut',
            }}
          >
            {step.status === 'complete' ? (
              <div
                className="group flex flex-col border-l-4 border-green-highlight py-2 pl-4 md:border-l-0 md:border-t-4 md:pb-0 md:pl-0 md:pt-4 transition-colors duration-150"
                role="group"
                aria-label={`Step ${step.id}: ${step.name} - Completed`}
              >
                <span className="text-sm font-medium text-gray-300 transition-colors duration-150">{step.id}</span>
                <span className="text-sm font-medium text-white">{step.name}</span>
              </div>
            ) : step.status === 'current' ? (
              <div
                aria-current="step"
                className="flex flex-col border-l-4 border-dataops-primary-blue py-2 pl-4 md:border-l-0 md:border-t-4 md:pb-0 md:pl-0 md:pt-4"
                role="group"
                aria-label={`Step ${step.id}: ${step.name} - Current step`}
              >
                <motion.span
                  className="text-sm font-medium text-dataops-primary-blue"
                  initial={{ opacity: 0.7 }}
                  animate={{ opacity: 1 }}
                  transition={{
                    duration: 1,
                    repeat: Infinity,
                    repeatType: 'reverse',
                  }}
                >
                  {step.id}
                </motion.span>
                <span className="text-sm font-medium text-white">{step.name}</span>
              </div>
            ) : (
              <div
                className="group flex flex-col border-l-4 border-gray-500 py-2 pl-4 md:border-l-0 md:border-t-4 md:pb-0 md:pl-0 md:pt-4"
                role="group"
                aria-label={`Step ${step.id}: ${step.name} - Not started`}
              >
                <span className="text-sm font-medium text-gray-400">{step.id}</span>
                <span className="text-sm font-medium text-gray-400">{step.name}</span>
              </div>
            )}
          </motion.li>
        ))}
      </ol>
    </nav>
  );
};

export default OnboardingProgressV2;
